<template>
    <ClientOnly>
        <VJoyDialog
            v-if="currentCountryHost && selectedHost"
            id="host-redirection-dialog"
            :cancel-text="isMaltComHostCountrySelected ? undefined : cancelLabel"
            :confirm-text="isMaltComHostCountrySelected ? cancelLabel : redirectionLabel"
            :open="!!selectedHost"
            size="large"
            pattern-confirm
            @dialog:confirm="handleClickOnConfirm"
            @dialog:hide="handleClickOnCancel"
        >
            <template #dialog-header>
                {{
                    isMaltComHostCountrySelected
                        ? t('host.redirection.dialog.host-com.title')
                        : t('host.redirection.dialog.title', [currentCountryDisplayName])
                }}
            </template>

            <template #dialog-subheader>
                {{
                    isMaltComHostCountrySelected
                        ? t('host.redirection.dialog.host-com.subtitle')
                        : t('host.redirection.dialog.subtitle', [currentCountryDisplayName])
                }}
            </template>

            <template #dialog-body>
                <VJoyWrapper item-stretch justify="space-between">
                    <VJoySelect
                        data-testid="country-select"
                        :model-value="selectedSupportedHost?.countryCode"
                        :label="t('host.redirection.dialog.country.select.label')"
                        @update:model-value="onCountryUpdate"
                    >
                        <option v-for="host in sortedHosts" :key="host.countryCode" :value="host.countryCode">
                            {{ host.countryName }}
                        </option>
                    </VJoySelect>
                    <VJoySelect
                        v-if="!!selectedSupportedHost"
                        v-model="selectedHost"
                        data-testid="language-select"
                        :label="t('host.redirection.dialog.language.select.label')"
                    >
                        <option v-for="language in selectedSupportedHost.languages" :key="language.language" :value="language.host">
                            {{ language.label }}
                        </option>
                    </VJoySelect>
                </VJoyWrapper>
            </template>
        </VJoyDialog>
    </ClientOnly>
</template>

<script lang="ts" setup>
    import {
        computed,
        type Host,
        type Language,
        navigateTo,
        ref,
        useDataTracking,
        useFormat,
        useHosts,
        useLocale,
        useRuntimeConfig,
        useTranslation,
    } from '#imports';
    import {VJoyDialog, VJoySelect, VJoyWrapper} from '@maltjoy/core-vue';

    // Composables
    const {countryCode, isMultiLocaleHost, languageCode, locale} = useLocale();
    const {t} = useTranslation();
    const {getHosts} = useHosts();
    const {formatCountryName, formatLanguageName} = useFormat();
    const {track} = useDataTracking();
    const runtimeConfig = useRuntimeConfig();
    const acceptLanguageLang = navigator.language ? navigator.language.slice(0, 2) : null;
    const supportedLocalePrefixes = (runtimeConfig.public['@malt/nuxt-i18n-module']?.supportedLocalePrefixes ?? []) as {
        code: string;
        prefix: string;
    }[];

    const hosts = await getHosts();

    const translateHostAndAddLanguages = (host: Host) => ({
        ...host,
        countryName: formatCountryName(host, t('host.redirection.dialog.host-com.country.select.label')),
        languages: host.languages
            .map((language) => ({...language, label: formatLanguageName(language)}))
            .sort((a, b) => a.label.localeCompare(b.label)),
    });

    // Computed values
    // Hosts
    const maltComHost = computed<Host>(
        () =>
            hosts.find((host) => !host.countryCode && !host.countryName) ?? {
                countryCode: '',
                countryName: 'None',
                languages: [{host: 'malt.com', language: 'en', label: 'English', locale: 'en'}],
            },
    );
    const filteredHosts = computed<Host[]>(() =>
        [...hosts].filter((host) => !!host.countryName && !!host.countryCode).map(translateHostAndAddLanguages),
    );
    const sortedHosts = computed<Host[]>(() => {
        return [
            ...filteredHosts.value.slice().sort((a, b) => a.countryName.localeCompare(b.countryName)),
            translateHostAndAddLanguages(maltComHost.value),
        ];
    });

    // Current country host
    const currentCountryHost = computed<Host | undefined>(() => sortedHosts.value.find((host) => host.countryCode === countryCode.value));
    const currentCountryDisplayName = computed<string>(() => formatCountryName(currentCountryHost.value!));
    const currentCountryHostLanguages = computed<Language[]>(() => currentCountryHost.value?.languages ?? []);
    const guessedHostLanguage = computed<Language | undefined>(
        () =>
            (acceptLanguageLang ? currentCountryHostLanguages.value.find(({language}) => language === acceptLanguageLang) : null) ??
            currentCountryHostLanguages.value.find(({language}) => language === countryCode.value?.toLowerCase()) ??
            currentCountryHostLanguages.value.find(({language}) => language === languageCode.value) ??
            currentCountryHostLanguages.value[0],
    );

    const MONTH_IN_MS = 1000 * 60 * 60 * 24 * 30;
    // www.malt.com serves these locales itself under a URL prefix (e.g. en-GB on /en-gb), so no country host is needed
    const isLocaleServedByMultiLocaleHost = computed<boolean>(
        () => isMultiLocaleHost.value && supportedLocalePrefixes.some(({code}) => code.toLowerCase() === locale.value.toLowerCase()),
    );
    const ignoreRedirection = computed<boolean>(() => {
        const {host, date = 0} = JSON.parse(localStorage?.getItem('selected-host:v1') ?? '{}');
        return (
            isLocaleServedByMultiLocaleHost.value ||
            // if the current host is targeting the same country but in a different language, ignore the redirection
            currentCountryHostLanguages.value.some(({host}) => host === window.location.host) ||
            // if the current host was already explicitly selected by the user, ignore the redirection
            (host === window.location.host && new Date().getTime() - date < MONTH_IN_MS)
        );
    });

    const selectedSupportedHost = computed<Host | undefined>(() =>
        sortedHosts.value.find((host) => host.languages.some((language) => language.host === selectedHost.value)),
    );
    const selectedSupportedHostLanguage = computed<Language | undefined>(() =>
        selectedSupportedHost.value?.languages.find((language) => language.host === selectedHost.value),
    );

    const cancelLabel = computed<string>(() => t('host.redirection.dialog.cancel.cta', [window.location.host]));
    const redirectionLabel = computed<string>(() => t('host.redirection.dialog.confirm.cta', [selectedHost.value]));
    const isMaltComHostCountrySelected = computed<boolean>(() => !!selectedSupportedHost.value && !selectedSupportedHost.value.countryCode);

    // Refs
    const selectedHost = ref<string | undefined>(ignoreRedirection.value ? undefined : guessedHostLanguage.value?.host);

    // Handlers
    async function handleClickOnCancel() {
        await cancelRedirection();
    }
    async function handleClickOnConfirm() {
        if (isMaltComHostCountrySelected.value) {
            await cancelRedirection();
            return;
        }
        await triggerRedirection();
    }

    function onCountryUpdate(countryCode: string) {
        const host = hosts.find((host) => host.countryCode === countryCode);

        if (host) {
            const defaultLanguage = host.languages.find(({language}) => language === countryCode.toLowerCase()) ?? host.languages[0];

            selectedHost.value = defaultLanguage?.host;
        }
    }

    async function triggerRedirection() {
        if (selectedHost.value === undefined) {
            return;
        }

        await track('local_redirection_pop_up_action_clicked', {
            action_clicked: 'take_me_to_local_domain',
            suggested_domain: guessedHostLanguage.value?.locale,
            redirection_domain: selectedSupportedHostLanguage.value?.locale,
        });

        const url = new URL(window.location.href);
        url.host = selectedHost.value;

        await navigateTo(url.toString(), {external: true});
    }

    async function cancelRedirection() {
        localStorage?.setItem(
            'selected-host:v1',
            JSON.stringify({
                host: window.location.host,
                date: new Date().getTime(),
            }),
        );
        selectedHost.value = undefined;

        await track('local_redirection_pop_up_action_clicked', {
            action_clicked: 'close_pop_up',
            suggested_domain: guessedHostLanguage.value?.locale,
        });
    }
</script>
